import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const parseRows = (raw) => {
  try { const r = JSON.parse(raw || "[]"); return Array.isArray(r) ? r : []; } catch { return []; }
};

// Soft figure drawing — ivory background, dusty lavender stroke, with annotated bust/waist/hip tape lines
const FigureDiagram = () => (
  <svg viewBox="0 0 220 400" className="w-full max-w-[260px] mx-auto" aria-hidden="true" data-testid="figure-diagram">
    {/* Body outline */}
    <g fill="none" stroke="#C3B1C5" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      {/* head */}
      <ellipse cx="110" cy="40" rx="22" ry="26" />
      {/* neck */}
      <path d="M100 65 Q110 72 120 65" />
      {/* shoulders + arms */}
      <path d="M70 90 Q110 78 150 90" />
      <path d="M70 90 Q58 150 62 220" />
      <path d="M150 90 Q162 150 158 220" />
      {/* torso */}
      <path d="M70 90 Q75 160 90 230" />
      <path d="M150 90 Q145 160 130 230" />
      {/* hip to legs */}
      <path d="M90 230 Q82 300 90 380" />
      <path d="M130 230 Q138 300 130 380" />
      {/* feet */}
      <path d="M82 380 L100 384" />
      <path d="M122 384 L140 380" />
    </g>

    {/* Tape measure lines */}
    {/* bust */}
    <g>
      <line x1="56" y1="118" x2="164" y2="118" stroke="#4A464A" strokeWidth="1" strokeDasharray="3 3" />
      <circle cx="56" cy="118" r="2.2" fill="#4A464A" />
      <circle cx="164" cy="118" r="2.2" fill="#4A464A" />
      <text x="172" y="121" fontFamily="Manrope" fontSize="10" letterSpacing="2" fill="#4A464A">BUST</text>
    </g>
    {/* waist */}
    <g>
      <line x1="76" y1="178" x2="144" y2="178" stroke="#4A464A" strokeWidth="1" strokeDasharray="3 3" />
      <circle cx="76" cy="178" r="2.2" fill="#4A464A" />
      <circle cx="144" cy="178" r="2.2" fill="#4A464A" />
      <text x="150" y="181" fontFamily="Manrope" fontSize="10" letterSpacing="2" fill="#4A464A">WAIST</text>
    </g>
    {/* hip */}
    <g>
      <line x1="68" y1="232" x2="152" y2="232" stroke="#4A464A" strokeWidth="1" strokeDasharray="3 3" />
      <circle cx="68" cy="232" r="2.2" fill="#4A464A" />
      <circle cx="152" cy="232" r="2.2" fill="#4A464A" />
      <text x="160" y="235" fontFamily="Manrope" fontSize="10" letterSpacing="2" fill="#4A464A">HIP</text>
    </g>
  </svg>
);

export default function SizeGuide() {
  const [s, setS] = useState({});

  useEffect(() => {
    api.get("/settings").then(({ data }) => setS(data)).catch(() => {});
  }, []);

  const rows = parseRows(s.size_guide_rows_json);
  const notes = [s.size_guide_note_1, s.size_guide_note_2, s.size_guide_note_3].filter(n => (n || "").trim());

  return (
    <div data-testid="size-guide-page" className="pb-24">
      {/* Header */}
      <section className="px-6 md:px-10 pt-16 md:pt-24 pb-12 text-center max-w-3xl mx-auto">
        <div className="text-[11px] tracking-editorial uppercase text-[#C3B1C5] mb-4">◇ Fit & Measurements</div>
        <h1 className="font-serif-display text-[46px] md:text-[64px] leading-[1.05] tracking-tight">Size Guide</h1>
        <p className="mt-6 text-[14px] md:text-[15px] leading-relaxed text-[#4A464A]/80" data-testid="size-guide-intro">
          {s.size_guide_intro || "Our sizes run in a relaxed Asian fit. Find your size using your body measurements below."}
        </p>
      </section>

      <div className="max-w-5xl mx-auto px-6 md:px-10 grid md:grid-cols-5 gap-10 md:gap-14">
        {/* Table */}
        <div className="md:col-span-3">
          <div className="text-[10px] uppercase tracking-editorial text-[#4A464A]/60 mb-3">Body measurements · cm</div>

          {/* Desktop table */}
          <div className="hidden sm:block border" style={{ borderColor: "#EBE5ED" }}>
            <div className="grid grid-cols-4 text-[11px] uppercase tracking-editorial px-4 py-3 border-b" style={{ borderColor: "#EBE5ED", background: "#EBE5ED55" }}>
              <div>Size</div><div>Bust</div><div>Waist</div><div>Hip</div>
            </div>
            {rows.map((r, i) => r.bust_only ? (
              <div key={i} data-testid={`size-row-${i}`} className="grid grid-cols-4 text-[14px] px-4 py-4 border-b last:border-b-0" style={{ borderColor: "#EBE5ED" }}>
                <div className="font-serif-display text-[16px]">{r.size}</div>
                <div className="col-span-3 text-[#4A464A]/85 italic">{r.bust}</div>
              </div>
            ) : (
              <div key={i} data-testid={`size-row-${i}`} className="grid grid-cols-4 text-[14px] px-4 py-4 border-b last:border-b-0" style={{ borderColor: "#EBE5ED" }}>
                <div className="font-serif-display text-[16px]">{r.size}</div>
                <div className="text-[#4A464A]/85">{r.bust}</div>
                <div className="text-[#4A464A]/85">{r.waist}</div>
                <div className="text-[#4A464A]/85">{r.hip}</div>
              </div>
            ))}
          </div>

          {/* Mobile stacked cards */}
          <div className="sm:hidden space-y-3">
            {rows.map((r, i) => (
              <div key={i} className="border p-4" style={{ borderColor: "#EBE5ED" }} data-testid={`size-card-${i}`}>
                <div className="font-serif-display text-[22px]">{r.size}</div>
                <div className="mt-3 space-y-1.5 text-[13px]">
                  {r.bust_only ? (
                    <div className="italic text-[#4A464A]/80">{r.bust}</div>
                  ) : (
                    <>
                      <div className="flex justify-between"><span className="text-[#4A464A]/60 uppercase tracking-editorial text-[10px]">Bust</span><span>{r.bust}</span></div>
                      <div className="flex justify-between"><span className="text-[#4A464A]/60 uppercase tracking-editorial text-[10px]">Waist</span><span>{r.waist}</span></div>
                      <div className="flex justify-between"><span className="text-[#4A464A]/60 uppercase tracking-editorial text-[10px]">Hip</span><span>{r.hip}</span></div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Notes */}
          <ul className="mt-8 space-y-2.5 text-[13px] italic text-[#4A464A]/75 leading-relaxed" data-testid="size-guide-notes">
            {notes.map((n, i) => <li key={i} className="flex gap-2"><span className="text-[#C3B1C5]">·</span><span>{n}</span></li>)}
          </ul>
        </div>

        {/* How to measure + figure */}
        <div className="md:col-span-2">
          <div className="border p-6 md:p-8" style={{ borderColor: "#EBE5ED", background: "#EBE5ED22" }}>
            <div className="font-serif-display text-[22px] mb-4">How to measure</div>
            <FigureDiagram />
            <ul className="mt-6 space-y-3 text-[13px] leading-relaxed text-[#4A464A]/85">
              <li className="flex gap-3"><span className="text-[#C3B1C5] mt-0.5">◇</span><span>{s.size_guide_how_bust || "Bust: around the fullest part of your chest, tape kept level."}</span></li>
              <li className="flex gap-3"><span className="text-[#C3B1C5] mt-0.5">◇</span><span>{s.size_guide_how_waist || "Waist: around the narrowest part of your waist."}</span></li>
              <li className="flex gap-3"><span className="text-[#C3B1C5] mt-0.5">◇</span><span>{s.size_guide_how_hip || "Hip: around the fullest part of your hips."}</span></li>
            </ul>
          </div>
        </div>
      </div>

      <div className="divider-thin w-24 mx-auto mt-20" />
      <div className="text-center mt-6 text-[12px] italic text-[#C3B1C5]">one look, effortlessly you.</div>
    </div>
  );
}
